import React, { useState, useEffect } from "react";
import { ShareIcon } from "./ShareIcon";
import { ShareModal } from "./ShareModal";
import { isMobileDevice, isTouchDevice } from "~/lib/share-utils";
import type { ShareContent } from "~/lib/share-utils";

interface ShareButtonProps {
  content: ShareContent;
  variant?: "primary" | "secondary" | "ghost";
  size?: "sm" | "md" | "lg";
  showText?: boolean;
  className?: string;
  mobileOptimized?: boolean;
}

export function ShareButton({
  content,
  variant = "ghost",
  size = "md",
  showText = true,
  className = "",
  mobileOptimized = true,
}: ShareButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [isTouch, setIsTouch] = useState(false);

  useEffect(() => {
    // 클라이언트에서만 실행
    setIsMobile(isMobileDevice());
    setIsTouch(isTouchDevice());
  }, []);

  const baseClasses =
    "inline-flex items-center justify-center font-medium rounded-md transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 active:scale-95";

  const variantClasses = {
    primary:
      "bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500 shadow-sm hover:shadow-md",
    secondary:
      "bg-gray-200 text-gray-900 hover:bg-gray-300 focus:ring-gray-500 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600 shadow-sm hover:shadow-md",
    ghost:
      "text-gray-500 hover:text-gray-700 hover:bg-gray-100 focus:ring-gray-500 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-800",
  };

  // 모바일에서 터치 친화적인 크기 조정
  const sizeClasses = {
    sm:
      isMobile && mobileOptimized
        ? "px-3 py-2 text-sm gap-2 min-h-[44px]"
        : "px-2 py-1 text-sm gap-1",
    md:
      isMobile && mobileOptimized
        ? "px-4 py-3 text-base gap-2 min-h-[48px]"
        : "px-3 py-2 text-sm gap-2",
    lg:
      isMobile && mobileOptimized
        ? "px-6 py-4 text-lg gap-3 min-h-[52px]"
        : "px-4 py-2 text-base gap-2",
  };

  const buttonClasses = `${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`;

  // 터치 디바이스에서 추가 스타일
  const touchStyles = isTouch ? "touch-manipulation select-none" : "";

  return (
    <>
      <button
        onClick={() => setIsModalOpen(true)}
        className={`${buttonClasses} ${touchStyles}`}
        aria-label="콘텐츠 공유하기"
        aria-describedby="share-button-desc"
        type="button"
      >
        <ShareIcon
          platform="native"
          size={size === "lg" ? "md" : "sm"}
          aria-hidden="true"
        />
        {showText && <span>공유</span>}
      </button>

      {/* 스크린 리더용 설명 */}
      <span id="share-button-desc" className="sr-only">
        {content.title}을(를) 소셜미디어나 다른 앱으로 공유할 수 있습니다
      </span>

      <ShareModal
        content={content}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
}
